'use client'

import { type FormEventHandler, useEffect, useRef, useState } from 'react'

type PrepareState = null | { sessionId: string; verificationCode: string } | { error: string }

type UseSmartIdFlowOptions = {
  onSuccess: (accessToken: string) => void | Promise<void>
  messages: {
    invalidRequest: string
    prepareError: string
    performError: string
    missingSessionId: string
  }
}

const SMART_ID_STATUS_PATH = '/api/auth/smart-id/status'
const SMART_ID_PREPARE_PATH = '/api/auth/smart-id/prepare'
const SMART_ID_POLL_DELAY_MS = 3_000
const SMART_ID_FLOW_TIMEOUT_MS = 5 * 60 * 1000
export const useSmartIdFlow = ({ onSuccess, messages }: UseSmartIdFlowOptions) => {
  const [prepareState, setPrepareState] = useState<PrepareState>(null)
  const [isPreparing, setIsPreparing] = useState(false)
  const [isPolling, setIsPolling] = useState(false)
  const [performError, setPerformError] = useState<string | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollAbortRef = useRef<AbortController | null>(null)
  const sessionDeadlineRef = useRef<number | null>(null)
  const sessionSucceededRef = useRef(false)
  const onSuccessRef = useRef(onSuccess)
  const messagesRef = useRef(messages)

  useEffect(() => {
    onSuccessRef.current = onSuccess
  }, [onSuccess])

  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  const sessionId = prepareState && 'sessionId' in prepareState ? prepareState.sessionId : null
  const verificationCode = prepareState && 'verificationCode' in prepareState ? prepareState.verificationCode : null
  const prepareError = prepareState && 'error' in prepareState ? prepareState.error : null
  const errorMessage = performError ?? prepareError
  const isWaitingForConfirmation = Boolean(sessionId && verificationCode && !performError)
  const isLoading = isPreparing || isPolling || isWaitingForConfirmation

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current)
      pollAbortRef.current?.abort()
    }
  }, [])

  useEffect(() => {
    if (!sessionId || performError) return

    let cancelled = false

    const clearPendingPoll = () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }

    const scheduleNextPoll = () => {
      if (cancelled) return
      clearPendingPoll()
      pollTimerRef.current = setTimeout(() => {
        void pollStatus()
      }, SMART_ID_POLL_DELAY_MS)
    }

    const pollStatus = async () => {
      if (cancelled || sessionSucceededRef.current) return

      const deadline = sessionDeadlineRef.current
      if (deadline !== null && Date.now() >= deadline) {
        setIsPolling(false)
        setPerformError(messagesRef.current.performError)
        return
      }

      pollAbortRef.current?.abort()
      const controller = new AbortController()
      pollAbortRef.current = controller
      setIsPolling(true)

      try {
        const response = await fetch(SMART_ID_STATUS_PATH, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
          cache: 'no-store',
          signal: controller.signal,
        })

        const payload = (await response.json().catch(() => null)) as {
          status?: 'pending' | 'success'
          accessToken?: string
          message?: string
        } | null

        if (cancelled || controller.signal.aborted) return

        if (!response.ok) {
          setIsPolling(false)
          setPerformError(payload?.message ?? messagesRef.current.performError)
          return
        }

        if (payload?.status === 'success' && payload.accessToken) {
          sessionSucceededRef.current = true
          setIsPolling(false)
          await onSuccessRef.current(payload.accessToken)
          return
        }

        setIsPolling(false)
        scheduleNextPoll()
      } catch (error) {
        if (cancelled || controller.signal.aborted) return

        if (error instanceof DOMException && error.name === 'AbortError') return

        setIsPolling(false)
        scheduleNextPoll()
      }
    }

    if (sessionDeadlineRef.current === null) {
      sessionDeadlineRef.current = Date.now() + SMART_ID_FLOW_TIMEOUT_MS
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      if (sessionSucceededRef.current) return
      clearPendingPoll()
      void pollStatus()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    void pollStatus()

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearPendingPoll()
      pollAbortRef.current?.abort()
    }
  }, [sessionId, performError])

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()
    setIsPreparing(true)
    setPerformError(null)
    setPrepareState(null)
    sessionDeadlineRef.current = null
    sessionSucceededRef.current = false

    const formData = new FormData(event.currentTarget)
    const payload = {
      personalIdentificationNumber: String(formData.get('personalIdentificationNumber') ?? ''),
      country: String(formData.get('country') ?? 'EE'),
    }

    try {
      const response = await fetch(SMART_ID_PREPARE_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        cache: 'no-store',
      })

      const data = (await response.json().catch(() => null)) as {
        sessionId?: string
        verificationCode?: string
        message?: string
      } | null

      if (!response.ok) {
        setPrepareState({ error: data?.message ?? messagesRef.current.prepareError })
        return
      }

      if (!data?.sessionId || !data.verificationCode) {
        setPrepareState({ error: data?.message ?? messagesRef.current.invalidRequest })
        return
      }
      setPrepareState({ sessionId: data.sessionId, verificationCode: data.verificationCode })
    } catch (error) {
      setPrepareState({ error: messagesRef.current.prepareError })
    } finally {
      setIsPreparing(false)
    }
  }

  return {
    errorMessage,
    handleSubmit,
    isLoading,
    isPreparing,
    isWaitingForConfirmation,
    verificationCode,
  }
}
